import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { Message, translator } from 'mage-i18n';
import {
  AlertBlockWarning,
  IllustrationErrorUFO,
  Link,
  Typography,
  SpacerVertical,
} from '@klarna/bubble-ui';
import { analytics, HorizontalWrapper } from 'moet-core';
import { ContainerTemplate, MessagingStep } from '@/components';
import BackButton from '@/components/BackButton';
import { getMerchantSensitiveGood, getSelectedCountry } from '@/selectors/data';
import { getSelectedPlatform } from '@/selectors/platforms';
import { DEFAULT_SENSITIVE_GOODS_VALUE } from '@/containers/SensitiveGoods/constants';
import { getMerchantAwareArrayTranslator } from '@/selectors/merchant';

import { ANALYTICS_EVENTS } from './analyticsEvents';

const tPrefix = 'products-fe.containers.we_are_sorry';
const goodsPrefix = 'products-fe.containers.sensitive_goods.goodsCategories';

const styles = {
  bolder: {
    fontSize: '19px',
    fontWeight: 700,
  },
};

export const WeAreSorryInner = ({
  merchantSensitiveGoods,
  platform,
  t,
  tArray,
  onBack,
  country,
}) => {
  const platformAlertContent = tArray(`${tPrefix}.platform_alert.${platform?.key}`);
  const platformAlert = platformAlertContent ? (
    <div>
      {platformAlertContent
        .map(({ text, link }, index) =>
          link ? (
            <Link key={index} href={link} target="_blank">
              {text}
            </Link>
          ) : (
            <span key={index}>{text}</span>
          ),
        )
        .reduce((prev, curr) => [prev, ' ', curr])}
    </div>
  ) : null;

  return (
    <ContainerTemplate>
      <MessagingStep illustration={<IllustrationErrorUFO />} title={[t(`${tPrefix}.title_new`)]}>
        <SpacerVertical spaceToken="space/150" />
        {platformAlert ? (
          <>
            <AlertBlockWarning content={[platformAlert]} />
            <SpacerVertical spaceToken="space/400" />
          </>
        ) : null}
        <Typography textToken="text-style/text/super-paragraphs/tall/regular">
          {merchantSensitiveGoods && merchantSensitiveGoods !== DEFAULT_SENSITIVE_GOODS_VALUE && (
            <>
              <Message path={`${tPrefix}.message_1_sensitive_goods`} />
              <br />
              <span style={styles.bolder}>
                {t(`${goodsPrefix}.${merchantSensitiveGoods}.title_2`)}
              </span>
              <br />
            </>
          )}
          {platform?.name && (
            <>
              <Message path={`${tPrefix}.message_1_platform`} />
              <br />
              <span style={styles.bolder}>{platform.name}</span>
              <br />
            </>
          )}
          {!platform && !merchantSensitiveGoods && (
            <>
              <Message path={`${tPrefix}.message_1_unsupported_country`} />
              <br />
              <span style={styles.bolder}>{country.label}</span>
              <br />
            </>
          )}
          <br />
          <div>
            <Message path={`${tPrefix}.message_3`} />
            <Link
              textToken="text-style/text/super-paragraphs/tall/regular"
              onPress={() => {
                analytics.logEvent(ANALYTICS_EVENTS.partnersPageLink);
              }}
              href={t(`${tPrefix}.psp_link`)}
              target="_blank"
            >
              {' '}
              <Message path={`${tPrefix}.message_4`} />{' '}
            </Link>
            <Message path={`${tPrefix}.message_5`} />
          </div>
        </Typography>
      </MessagingStep>
      <SpacerVertical spaceToken="space/600" />
      <HorizontalWrapper display="inline-flex" alignItems="center">
        <BackButton tabIndex={2} onClick={onBack} />
      </HorizontalWrapper>
    </ContainerTemplate>
  );
};

WeAreSorryInner.propTypes = {
  t: PropTypes.func.isRequired,
  tArray: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  country: PropTypes.object,
  merchantSensitiveGoods: PropTypes.string,
  platform: PropTypes.object,
};

export const WeAreSorry = compose(
  connect(state => ({
    t: translator(state),
    tArray: getMerchantAwareArrayTranslator(state),
    merchantSensitiveGoods: getMerchantSensitiveGood(state),
    platform: getSelectedPlatform(state),
    country: getSelectedCountry(state),
  })),
)(WeAreSorryInner);
